<template>
  <div class="form-container">
    <div class="modal-header">
      <h1 class="text-sm sm:text-base font-bold">{{ editData ? '문의 수정' : '문의 작성' }}</h1>
      <p class="text-[10px] sm:text-xs text-gray-600">매장 관련 문의사항을 작성해주세요.</p>
    </div>

    <h2 class="text-xs sm:text-sm font-medium">카테고리 선택</h2>

    <div class="form-grid">
      <div class="form-group">
        <label class="text-[10px] sm:text-xs">브랜드<span class="text-red-500">*</span></label>
        <div class="brand-toggles">
          <label class="brand-toggle">
            <input type="radio" v-model="form.selectedBrand" value="agabang" class="mr-1 mb-1">
            <span class="text-[10px] sm:text-xs">아가방</span>
          </label>
          <label class="brand-toggle">
            <input type="radio" v-model="form.selectedBrand" value="ettoi" class="mr-1 mb-1">
            <span class="text-[10px] sm:text-xs">에뜨와</span>
          </label>
          <label class="brand-toggle">
            <input type="radio" v-model="form.selectedBrand" value="disney" class="mr-1 mb-1">
            <span class="text-[10px] sm:text-xs">디즈니</span>
          </label>
          <label class="brand-toggle">
            <input type="radio" v-model="form.selectedBrand" value="flex" class="mr-1 mb-1">
            <span class="text-[10px] sm:text-xs">플렉스</span>
          </label>
        </div>
        <p v-if="showErrors && !form.selectedBrand" class="error-message">브랜드를 선택해주세요.</p>
      </div>

      <div class="form-group">
        <label class="text-[10px] sm:text-xs">대분류</label>
        <select v-model="form.cat_id" class="form-control" @change="handleCatChange">
          <option value="">선택해주세요</option>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">
            {{ cat.name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="text-[10px] sm:text-xs">중분류</label>
        <select v-model="form.midcat_id" class="form-control" @change="handleMidcatChange" :disabled="!form.cat_id">
          <option value="">선택해주세요</option>
          <option v-for="midcat in midCategories" :key="midcat.id" :value="midcat.id">
            {{ midcat.name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="text-[10px] sm:text-xs">소분류</label>
        <select v-model="form.subcat_id" class="form-control" :disabled="!form.midcat_id">
          <option value="">선택해주세요</option>
          <option v-for="subcat in subCategories" :key="subcat.id" :value="subcat.id">
            {{ subcat.name }}
          </option>
        </select>
      </div>
    </div>

    <h2 class="text-xs sm:text-sm font-medium">내용 작성</h2>

    <div class="form-group">
      <label class="text-[10px] sm:text-xs">제목<span class="text-red-500">*</span></label>
      <input type="text" v-model="form.title" placeholder="제목을 입력해주세요" class="form-control" />
      <p v-if="showErrors && !form.title" class="error-message">제목을 입력해주세요.</p>
    </div>

    <div class="form-group">
      <label class="text-[10px] sm:text-xs">내용<span class="text-red-500">*</span></label>
      <textarea v-model="form.content" placeholder="요청에 대한 세부내용을 작성해주세요" class="form-control" rows="4"></textarea>
      <div class="char-count">{{ form.content.length }}/300자</div>
      <p v-if="showErrors && !form.content" class="error-message">내용을 입력해주세요.</p>
    </div>

    <h2 class="text-xs sm:text-sm font-medium">첨부파일</h2>

    <div class="file-upload" @click="triggerFileInput">
      <input type="file" ref="fileInput" @change="handleFileChange" multiple style="display: none"
        accept="image/jpeg,image/jpg,image/png,image/gif" />
      <div class="upload-content">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <span>이미지 파일 선택하기</span>
        <p class="upload-desc">(JPG, PNG, GIF / 최대 3개까지 가능)</p>
      </div>
    </div>

    <div class="file-list" v-if="files.length > 0">
      <div class="file-item" v-for="(file, index) in files" :key="index">
        <span class="file-name">{{ file.name }}</span>
        <button class="file-remove" @click.stop="removeFile(index)">×</button>
      </div>
    </div>

    <div class="form-footer">
      <button type="button" class="btn-cancel text-[10px] sm:text-xs" @click="cancel">취소</button>
      <button type="button" class="btn-submit text-[10px] sm:text-xs" @click="submitForm"
        :disabled="!isFormValid">저장</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import inquiryAPI from '@/api/inquiry/inquiry';

export default {
  name: 'InquiryForm',
  emits: ['close', 'submit'],
  
  props: {
    editData: {
      type: Object,
      default: null
    }
  },
  
  setup(props, { emit }) {
    const form = ref({
      title: '',
      content: '',
      cat_id: '',
      midcat_id: '',
      subcat_id: '',
      selectedBrand: '',
    });
    
    const files = ref([]);
    const fileInput = ref(null);
    const categories = ref([]);
    const midCategories = ref([]);
    const subCategories = ref([]);
    const showErrors = ref(false);
    
    const isFormValid = computed(() => {
      return form.value.title &&
             form.value.content &&
             form.value.subcat_id &&
             form.value.selectedBrand;
    });

    // 수정 모드일 때 초기 데이터 설정
    const initializeEditData = async () => {
      if (props.editData) {
        form.value = {
          title: props.editData.title,
          content: props.editData.content,
          cat_id: props.editData.cat_id,
          midcat_id: props.editData.midcat_id,
          subcat_id: props.editData.subcat_id,
          selectedBrand: props.editData.selectedBrand,
        };
        
        // 기존 첨부파일 설정 수정
        files.value = (props.editData.attachments || []).map(file => ({
          id: file.id,
          name: file.name,
          file_name: file.file_name,
          type: file.type,
          isExisting: true,  // 이 플래그로 기존 파일임을 표시
          existingFile: file
        }));
        
        // 카테고리 데이터 로드
        await loadCategories();
        
        if (form.value.cat_id) {
          const midCategoriesResponse = await inquiryAPI.getMidCategories(form.value.cat_id);
          midCategories.value = midCategoriesResponse.data;
          
          if (form.value.midcat_id) {
            const subCategoriesResponse = await inquiryAPI.getSubCategories(form.value.midcat_id);
            subCategories.value = subCategoriesResponse.data;
          }
        }
      }
    };

    // 카테고리 데이터 로드
    const loadCategories = async () => {
      try {
        const response = await inquiryAPI.getCategories();
        categories.value = response.data;
      } catch (error) {
        console.error('카테고리 로드 실패:', error);
      }
    };

    const handleCatChange = async () => {
      form.value.midcat_id = '';
      form.value.subcat_id = '';
      midCategories.value = [];
      subCategories.value = [];
      
      if (form.value.cat_id) {
        try {
          const response = await inquiryAPI.getMidCategories(form.value.cat_id);
          midCategories.value = response.data;
        } catch (error) {
          console.error('중분류 로드 실패:', error);
        }
      }
    };

    const handleMidcatChange = async () => {
      form.value.subcat_id = '';
      subCategories.value = [];
      
      if (form.value.midcat_id) {
        try {
          const response = await inquiryAPI.getSubCategories(form.value.midcat_id);
          subCategories.value = response.data;
          
          // 소분류가 하나만 있으면 자동 선택
          if (subCategories.value.length === 1) {
            form.value.subcat_id = subCategories.value[0].id;
          }
        } catch (error) {
          console.error('소분류 로드 실패:', error);
        }
      }
    };

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    const handleFileChange = (event) => {
      const newFiles = Array.from(event.target.files);
      
      if (files.value.length + newFiles.length > 3) {
        alert('최대 3개의 파일만 업로드할 수 있습니다.');
        return;
      }
      
      for (const file of newFiles) {
        if (!file.type.startsWith('image/')) {
          alert('이미지 파일만 업로드할 수 있습니다.');
          return;
        }

        if (file.size > 10 * 1024 * 1024) {
          alert('파일 크기는 10MB를 초과할 수 없습니다.');
          return;
        }
        
        files.value.push({
          name: file.name,
          size: file.size,
          file: file,
          originalFile: file
        });
      }
      
      event.target.value = '';
    };

    // 이미지 압축
    const compressImage = (file, isThumbnail = false) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // 썸네일인 경우 더 작은 크기로 압축
            let width = img.width;
            let height = img.height;
            if (isThumbnail) {
              // 썸네일은 최대 300x300 크기로
              if (width > 300) {
                height = Math.round((height * 300) / width);
                width = 300;
              }
              if (height > 300) {
                width = Math.round((width * 300) / height);
                height = 300;
              }
            } else {
              // 원본은 최대 1920x1080 크기로
              if (width > 1920) {
                height = Math.round((height * 1920) / width);
                width = 1920;
              }
              if (height > 1080) {
                width = Math.round((width * 1080) / height);
                height = 1080;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            // 압축된 이미지를 Blob으로 변환
            canvas.toBlob(
              (blob) => {
                resolve(new File([blob], file.name, {
                  type: 'image/png',
                  lastModified: Date.now(),
                }));
              },
              'image/png',
              // isThumbnail ? 0.3 : 0.6 // 썸네일은 더 높은 압축률 적용
              isThumbnail ? 0.2 : 0.4 // 썸네일은 0.2, 원본은 0.4로 압축률 증가
            );
          };
        };
      });
    };

    const removeFile = (index) => {
      files.value.splice(index, 1);
    };

    const submitForm = async () => {
      showErrors.value = true;
      
      if (!isFormValid.value) {
        return;
      }

      try {
        const formData = new FormData();
        formData.append('title', form.value.title);
        formData.append('content', form.value.content);
        formData.append('cat_id', form.value.cat_id);
        formData.append('midcat_id', form.value.midcat_id);
        formData.append('subcat_id', form.value.subcat_id);
        formData.append('is_agabang', form.value.selectedBrand === 'agabang' || form.value.selectedBrand === 'flex');
        formData.append('is_ettoi', form.value.selectedBrand === 'ettoi' || form.value.selectedBrand === 'flex');
        formData.append('is_disney', form.value.selectedBrand === 'disney' || form.value.selectedBrand === 'flex');
        formData.append('upload_year', new Date().getFullYear().toString());

        // 기존 파일 정보 추가
        const existingFiles = files.value
          .filter(file => file.isExisting)
          .map(file => ({
            id: file.id,
            file_name: file.file_name,
            type: file.type
          }));
        
        console.log('전송되는 기존 파일 정보:', existingFiles); // 디버깅용 로그
        formData.append('existing_files', JSON.stringify(existingFiles));

        // 새로운 파일만 압축하여 추가
        for (const file of files.value) {
          if (!file.isExisting) {
            const originalFile = await compressImage(file.originalFile, false);
            const thumbnailFile = await compressImage(file.originalFile, true);
            formData.append('files', originalFile);
            formData.append('thumbnails', thumbnailFile);
          }
        }

        let response;
        if (props.editData) {
          response = await inquiryAPI.updateInquiry(props.editData.id, formData);
        } else {
          response = await inquiryAPI.createInquiry(formData);
        }

        emit('submit', response.data);
        emit('close');
      } catch (error) {
        console.error('문의 등록 실패:', error);
        alert('문의 등록에 실패했습니다.');
      }
    };

    const cancel = () => {
      emit('close');
    };

    onMounted(async () => {
      await loadCategories();
      if (props.editData) {
        await initializeEditData();
      }
    });

    return {
      form,
      files,
      fileInput,
      categories,
      midCategories,
      subCategories,
      isFormValid,
      triggerFileInput,
      handleFileChange,
      removeFile,
      submitForm,
      cancel,
      handleCatChange,
      handleMidcatChange,
      showErrors
    };
  }
};
</script>

<style scoped>
.form-container {
  max-height: 90vh;
  overflow-y: auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.modal-header {
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}

.modal-header h1 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.modal-header p {
  font-size: 12px;
  color: #666;
  margin: 0;
}

.form-container::-webkit-scrollbar {
  width: 8px;
}

.form-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.form-container::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 4px;
}

h2 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 28px 0 16px 0;
  display: flex;
  align-items: center;
}

h2:first-child {
  margin-top: 0;
}

h2::before {
  content: '';
  display: inline-block;
  width: 2px;
  height: 12px;
  background-color: #1a73e8;
  margin-right: 8px;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: inline-block;
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
}

.brand-toggles {
  display: flex;
  gap: 24px;
  padding: 4px 0;
}

.brand-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.brand-toggle input[type="radio"] {
  width: 16px;
  height: 16px;
  margin: 0;
  border: 1.5px solid #999;
  border-radius: 3px;
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  cursor: pointer;
  vertical-align: middle;
  transition: all 0.2s;
}

.brand-toggle input[type="radio"]:checked {
  background-color: #1a73e8;
  border-color: #1a73e8;
}

.brand-toggle input[type="radio"]:checked::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.brand-toggle span {
  font-size: 13px;
  color: #333;
  padding-top: 1px;
}

.form-control {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  font-size: 13px;
  border: 1px solid #ddd;
  border-radius: 6px;
  color: #333;
  transition: all 0.2s;
}

.form-control:hover {
  border-color: #1a73e8;
}

.form-control:focus {
  border-color: #1a73e8;
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.1);
}

.form-control::placeholder {
  color: #999;
}

select.form-control {
  background-color: white;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%23666' d='M0 3l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
  appearance: none;
  cursor: pointer;
}

select.form-control:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

textarea.form-control {
  height: auto;
  min-height: 120px;
  padding: 12px;
  line-height: 1.5;
  resize: vertical;
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

.file-upload {
  padding: 24px;
  border: 1px dashed #ddd;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  background-color: #fafafa;
  transition: all 0.2s;
}

.file-upload:hover {
  border-color: #1a73e8;
  background-color: rgba(26, 115, 232, 0.02);
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-content svg {
  color: #1a73e8;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.file-upload:hover .upload-content svg {
  opacity: 1;
}

.upload-content span {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.upload-desc {
  font-size: 12px;
  color: #666;
  margin: 0;
}

.file-list {
  margin-top: 12px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #eee;
}

.file-name {
  flex: 1;
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-remove {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 18px;
  margin-left: 8px;
  transition: all 0.2s;
}

.file-remove:hover {
  color: #dc3545;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.btn-cancel,
.btn-submit {
  min-width: 70px;
  height: 36px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  padding: 0 16px;
  transition: all 0.2s;
}

.btn-cancel {
  background-color: #fff;
  border: 1px solid #ddd;
  color: #666;
}

.btn-cancel:hover {
  background-color: #f5f5f5;
  border-color: #ccc;
}

.btn-submit {
  background-color: #1a73e8;
  color: white;
  border: none;
}

.btn-submit:hover {
  background-color: #1557b0;
}

.btn-submit:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .form-container {
    padding: 16px;
    height: 100vh;
    max-height: 100vh;
  }

  .brand-toggles {
    gap: 16px;
    flex-wrap: wrap;
  }

  .form-control {
    height: 40px;
  }

  .btn-cancel,
  .btn-submit {
    min-width: 60px;
    height: 36px;
    font-size: 12px;
    padding: 0 12px;
  }

  .file-upload {
    padding: 20px;
  }

  .form-footer {
    position: sticky;
    bottom: 0;
    background: white;
    padding: 12px 0;
    margin-top: 24px;
  }
}

.error-message {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}
</style>